import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats, ThemePalette } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'gc-custom-calendar',
  templateUrl: './custom-calendar.component.html',
  styleUrls: ['./custom-calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomCalendarComponent implements OnInit {
  @Input() color: ThemePalette = 'primary';
  @Input() minDate: DateTime;
  @Input() maxDate: DateTime;
  @Input() selected: DateTime;
  @Input() availableDays: string[] = [];

  dateFilter: (day: DateTime) => boolean = undefined;

  selectedDate: DateTime;
  @HostBinding('class.gc-custom-calendar') customClass = true;
  @Output() selectedChange = new EventEmitter<DateTime>();
  headerComponent = CustomCalendarHeaderComponent;

  ngOnInit(): void {
    this.dateFilter = (day: DateTime) => this.availableDays.some((availableDay) => availableDay === day.toISODate());
  }

  @HostBinding('class.primary')
  get primary() {
    return this.color === 'primary';
  }
  @HostBinding('class.accent')
  get accent() {
    return this.color === 'accent';
  }

  onDateChange(date: DateTime) {
    this.selected = date;
    this.selectedChange.emit(date);
  }
}

@Component({
  selector: 'gc-custom-calendar-header',
  templateUrl: './custom-calendar-header.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomCalendarHeaderComponent<D> implements OnDestroy {
  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly calendar: MatCalendar<D>,
    private readonly dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private readonly dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef,
  ) {
    calendar.stateChanges.pipe(takeUntil(this.destroyed)).subscribe(() => cdr.markForCheck());
  }

  get periodLabel() {
    return this.dateAdapter.format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel);
  }

  get previousEnabled(): boolean {
    return !this.isSameView(this.calendar.activeDate, this.calendar.minDate);
  }

  get nextEnabled(): boolean {
    return !this.isSameView(this.calendar.activeDate, this.calendar.maxDate);
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  previousClicked() {
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1);
  }

  nextClicked() {
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1);
  }

  private isSameView(date1: D, date2: D): boolean {
    if (this.calendar.currentView === 'month') {
      return (
        this.dateAdapter.getYear(date1) === this.dateAdapter.getYear(date2) &&
        this.dateAdapter.getMonth(date1) === this.dateAdapter.getMonth(date2)
      );
    }
    if (this.calendar.currentView === 'year') {
      return this.dateAdapter.getYear(date1) === this.dateAdapter.getYear(date2);
    }
  }
}
